import axios from 'axios';
import { Demo, Snapshot, CreateDemoRequest } from '../types/Demo';

// API base URL configuration for nginx proxy
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/demo-site-builder/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000, // 10 minutes for large file uploads
  withCredentials: true, // Important for session cookies
});

export const apiService = {
  // Authentication
  async login(email: string, password: string): Promise<any> {
    const formData = new URLSearchParams();
    formData.append('email', email);
    formData.append('password', password);

    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  async logout(): Promise<any> {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  async checkAuth(): Promise<any> {
    const response = await api.get('/auth/me');
    return response.data;
  },
  // Demo operations
  async getDemos(): Promise<{ demos: Demo[] }> {
    const response = await api.get('/list-demos');
    return response.data;
  },

  async createDemo(data: CreateDemoRequest): Promise<any> {
    const response = await api.post('/create-demo', data);
    return response.data;
  },

  async deleteDemo(id: number): Promise<any> {
    const response = await api.delete(`/delete-demo/${id}`);
    return response.data;
  },

  // Snapshot operations
  async getSnapshots(): Promise<Snapshot[]> {
    const response = await api.get('/snapshots-with-preview');
    return response.data;
  },

  async createSnapshot(sourceDomain: string, snapshotName: string, screenshot?: File): Promise<any> {
    console.log('API: Creating snapshot request', { sourceDomain, snapshotName, hasScreenshot: !!screenshot });

    const formData = new FormData();
    formData.append('sourceDomain', sourceDomain);
    formData.append('snapshotName', snapshotName);

    if (screenshot) {
      formData.append('screenshot', screenshot);
      console.log('API: Added screenshot file to FormData', { fileName: screenshot.name, size: screenshot.size });
    }

    console.log('API: Making POST request to /create-snapshot');

    const response = await api.post('/create-snapshot', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('API: Response received', response.data);
    return response.data;
  },

  async uploadSnapshot(snapshotFile: File, snapshotName: string, onUploadProgress?: (progressEvent: any) => void): Promise<any> {
    console.log('API: Uploading snapshot', { snapshotName, fileName: snapshotFile.name, size: snapshotFile.size });

    const formData = new FormData();
    formData.append('snapshotFile', snapshotFile);
    formData.append('snapshotName', snapshotName);

    console.log('API: Making POST request to /upload-snapshot');

    const response = await api.post('/upload-snapshot', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: onUploadProgress,
    });

    console.log('API: Upload response received', response.data);
    return response.data;
  },

  async updateSnapshotName(snapshotId: number, displayName: string): Promise<any> {
    console.log('🌐 API: Updating snapshot display name', { snapshotId, displayName });
    console.log('🌐 API: Request URL:', `${API_BASE_URL}/snapshots/rename`);
    console.log('🌐 API: Request body:', JSON.stringify({ snapshotId, displayName }));

    const response = await api.put('/snapshots/rename', {
      snapshotId,
      displayName,
    });

    console.log('🌐 API: Display name update response received', response.data);
    return response.data;
  },

  async updateSnapshotScreenshot(snapshotId: number, screenshot: File): Promise<any> {
    console.log('🌐 API: Updating snapshot screenshot', { snapshotId, fileName: screenshot.name, size: screenshot.size });

    const formData = new FormData();
    formData.append('snapshotId', snapshotId.toString());
    formData.append('screenshot', screenshot);

    console.log('🌐 API: Making PUT request to /snapshots/screenshot');
    console.log('🌐 API: Form data - snapshotId:', snapshotId);

    const response = await api.put('/snapshots/screenshot', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    console.log('🌐 API: Screenshot update response received', response.data);
    return response.data;
  },

  async deleteSnapshot(snapshotId: number): Promise<any> {
    console.log('🌐 API: Deleting snapshot', { snapshotId });
    console.log('🌐 API: Request URL:', `${API_BASE_URL}/snapshots/${snapshotId}`);

    const response = await api.delete(`/snapshots/${snapshotId}`);

    console.log('🌐 API: Delete response received', response.data);
    return response.data;
  },

  async downloadSnapshot(snapshotName: string): Promise<void> {
    console.log('API: Downloading snapshot', { snapshotName });

    const response = await api.get(`/snapshots/download/${snapshotName}`, {
      responseType: 'blob', // Important for file download
    });

    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${snapshotName}.zip`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    console.log('API: Snapshot downloaded successfully');
  },

  // User operations
  async getUsersByDomain(domain: string): Promise<any> {
    console.log('API: Getting users for domain', { domain });
    const response = await api.get(`/user/domain/${domain}/users`);
    console.log('API: Users response received', response.data);
    return response.data;
  },

  async resetPassword(domain: string, email: string, password: string): Promise<any> {
    console.log('API: Resetting password', { domain, email });
    const response = await api.post('/user/reset-password', {
      domain,
      email,
      password,
    });
    console.log('API: Password reset response received', response.data);
    return response.data;
  },
};

export default api;